import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import FileMarkdownIcon from '@atlaskit/icon-lab/core/file-markdown';
import HistoryIcon from '@atlaskit/icon-lab/core/history';

const i18n = defineMessages({
	title: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.title',
		description:
			'Title of the modal dialog asking the user to confirm rich-text → markdown conversion',
		defaultMessage: 'Convert to Markdown?',
	},
	richContentWarning: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.rich-content-warning',
		description: 'Warning message explaining that rich formatting may not survive conversion',
		defaultMessage: 'Your content has rich text formatting that may be unsupported in markdown.',
	},
	versionHistoryInfo: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.version-history-info',
		description: 'Information message explaining that version history will be preserved',
		defaultMessage:
			'Converting may result in data loss, although you can always revert to this previous version in version history.',
	},
	createChildPageInfo: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.create-child-page-info',
		description:
			'Information message explaining that a child page can be created to preserve the existing rich text version',
		defaultMessage:
			'You can also convert to markdown by creating a child page – preserving this existing rich text version.',
	},
	cancel: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.cancel',
		description: 'Cancel button label in the markdown conversion confirmation dialog',
		defaultMessage: 'Cancel',
	},
	createChildPage: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.create-child',
		description:
			'Secondary action label that creates a child page containing the markdown version while leaving the current page untouched',
		defaultMessage: 'Create child page',
	},
	convertThisPage: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.convert-in-place',
		description: 'Primary action label that converts the current page in place',
		defaultMessage: 'Convert to Markdown',
	},
	errorMessage: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.error',
		description:
			'Inline error message shown inside the dialog when the markdown conversion request fails',
		defaultMessage: 'Something went wrong while converting this page. Please try again.',
	},
	heroAlt: {
		id: 'content-markdown-mode.convert-to-markdown-dialog.hero-alt',
		description:
			'Alternative text describing the illustration at the top of the markdown conversion confirmation dialog',
		defaultMessage: 'Illustration of a markdown mode toggle',
	},
});

const styles = cssMap({
	bodyStack: {
		paddingBlock: token('space.150'),
	},
	footer: {
		width: '100%',
	},
});
export type ConvertToMarkdownDialogProps = {
	/**
	 * Called when the user dismisses the dialog (close icon, escape key,
	 * blanket click, or Cancel button).
	 */
	onClose: () => void;
	/**
	 * Called when the user clicks "Convert this page". Should run the
	 * markdown-mode mutation and (temporarily) the FE-side document replace.
	 * Must return a promise that resolves to `{ success }` so the dialog can
	 * decide whether to surface the error state or close itself.
	 */
	onConfirm: () => Promise<{ success: boolean }>;
};

const ConvertToMarkdownDialogContents = ({ onClose, onConfirm }: ConvertToMarkdownDialogProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasError, setHasError] = useState(false);

	const handleConvert = useCallback(async () => {
		setHasError(false);
		setIsSubmitting(true);
		try {
			const { success } = await onConfirm();
			if (success) {
				onClose();
			} else {
				setHasError(true);
			}
		} catch {
			setHasError(true);
		} finally {
			setIsSubmitting(false);
		}
	}, [onConfirm, onClose]);

	return (
		<>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance="warning">
					<FormattedMessage {...i18n.title} />
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.100">
					<Inline space="space.100">
						<FileMarkdownIcon label="" spacing="spacious" />
						<FormattedMessage {...i18n.richContentWarning} />
					</Inline>
					<Inline space="space.100">
						<HistoryIcon label="" spacing="spacious" />
						<FormattedMessage {...i18n.versionHistoryInfo} />
					</Inline>
				</Stack>
				{hasError ? (
					<SectionMessage appearance="error" testId="convert-to-markdown-dialog-error">
						<FormattedMessage {...i18n.errorMessage} />
					</SectionMessage>
				) : null}
			</ModalBody>
			<ModalFooter>
				<Box xcss={styles.footer}>
					<Inline alignBlock="center" spread="space-between">
						<Button
							appearance="subtle"
							onClick={onClose}
							isDisabled={isSubmitting}
							testId="convert-to-markdown-dialog-cancel"
						>
							<FormattedMessage {...i18n.cancel} />
						</Button>
						<Inline space="space.100">
							<Button
								appearance="warning"
								onClick={handleConvert}
								isDisabled={isSubmitting}
								testId="convert-to-markdown-dialog-convert"
							>
								<FormattedMessage {...i18n.convertThisPage} />
							</Button>
						</Inline>
					</Inline>
				</Box>
			</ModalFooter>
		</>
	);
};

/**
 * Confirmation dialog for converting a Confluence page from rich text into
 * markdown mode. Only rendered when `isMarkdownConversionLossy(adf)` returns
 * `true` — when the document is safely round-trippable, the caller should
 * fire the mutation directly without ever showing this UI.
 */
export type ConvertToMarkdownDialogWrapperProps = ConvertToMarkdownDialogProps & {
	isOpen: boolean;
};

export const ConvertToMarkdownDialog = ({
	isOpen,
	onClose,
	onConfirm,
}: ConvertToMarkdownDialogWrapperProps) => (
	<ModalTransition>
		{isOpen ? (
			<Modal onClose={onClose} width="small" testId="convert-to-markdown-dialog">
				<ConvertToMarkdownDialogContents onClose={onClose} onConfirm={onConfirm} />
			</Modal>
		) : null}
	</ModalTransition>
);
